import { z } from 'zod';
import { requiredPhoneNumberSchema } from '@trashlab/api/src/domains/core/validation/phone-number-schema';
import { ServiceRequestStatus } from '@trashlab/api/src/domains/website/types/service-request';

export const websiteVariantCommonFieldsSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
});

export const websiteCommonFieldsSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  googlePlaceId: z.string().optional(),
  googleAnalyticsId: z.string().optional(),
  googleTagManagerId: z.string().optional(),
  enableGoogleAnalytics: z.boolean().optional(),
  enableEcommerce: z.boolean().optional(),
  enableAiChat: z.boolean().optional(),
  enableGoogleReviews: z.boolean().optional(),
  enableQuickQuote: z.boolean().optional(),
});

export const createWebsiteVariantSchema = z.object({
  websiteId: z.uuid(),
  ...websiteVariantCommonFieldsSchema.shape,
});

export const updateWebsiteVariantSchema = z.object({
  id: z.uuid(),
  fieldsToUpdate: websiteVariantCommonFieldsSchema.partial(),
});

export const createWebsiteSchema = z.object({
  tenantId: z.uuid(),
  isTestOrg: z.boolean().default(false),
  websiteName: z.string().min(1, 'Website name is required'),
  googlePlaceId: z.string().optional(),
});

export const updateWebsiteSchema = z.object({
  id: z.uuid(),
  fieldsToUpdate: websiteCommonFieldsSchema.partial(),
});

export const submitServiceRequestSchema = z.object({
  tenantId: z.uuid(),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.email(),
  phone: requiredPhoneNumberSchema,
  serviceRequested: z.string().max(255),
  preferredServiceDate: z.string().optional(),
  websiteId: z.uuid(),
  domainId: z.uuid(),
});

export const updateServiceRequestStatusSchema = z.object({
  id: z.uuid(),
  status: z.nativeEnum(ServiceRequestStatus),
});

// Domain validation regex - supports standard domains and subdomains
// Does NOT allow .sites.trashlab.com domains (those are managed internally)
export const DOMAIN_REGEX =
  /^(?!.*\.sites\.trashlab\.com$)([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

/**
 * Shared domain validation schema for custom domain operations.
 * Validates format and blocks internal .sites.trashlab.com domains.
 */
export const customDomainSchema = z
  .string()
  .min(4)
  .max(255)
  .refine((val) => DOMAIN_REGEX.test(val), {
    message:
      'Invalid domain format. Must be a valid domain name (e.g., www.example.com)',
  })
  .refine((val) => !val.endsWith('.sites.trashlab.com'), {
    message: 'Cannot use *.sites.trashlab.com domains as custom domains',
  });

export const sendgridSubdomainSchema = z
  .string()
  .trim()
  .min(1)
  .max(63)
  .regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i, {
    message:
      'SendGrid subdomain must be a valid DNS label, such as "em" or "mail".',
  })
  .transform((value) => value.toLowerCase());
